import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket, AlertCircle, Loader2, MessageSquare, Download, ArrowLeft,
  Send, RefreshCw, Search, Filter, Clock, CheckCircle2, XCircle,
  PauseCircle, AlertTriangle, User, Building2, Mail, Phone, Tag,
  Calendar, ChevronDown,
} from 'lucide-react';
import { Button } from '../ui/button';

interface AdminTicketsTabProps {
  token: string;
}

interface TicketRow {
  id: string;
  clientId: string;
  clientName?: string;
  title: string;
  description: string;
  queryName: string;
  companyName: string;
  emailAddress: string;
  phoneNumber: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed' | 'on-hold';
  assignedTo?: string;
  resolutionNotes?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
}

interface TicketDetail extends TicketRow {
  attachments: Array<{ id: string; fileName: string; fileType: string; fileSize: number; filePath: string; createdAt: string }>;
  comments: Array<{ id: string; text: string; commentedBy: string; isInternal: boolean; createdAt: string }>;
}

const PRIORITY_STYLES: Record<string, string> = {
  low:      'bg-blue-500/10 text-blue-500 border border-blue-500/30',
  medium:   'bg-yellow-500/10 text-yellow-500 border border-yellow-500/30',
  high:     'bg-orange-500/10 text-orange-500 border border-orange-500/30',
  critical: 'bg-red-500/10 text-red-500 border border-red-500/30',
};

const STATUS_STYLES: Record<string, string> = {
  open:          'bg-blue-500/10 text-blue-500 border border-blue-500/30',
  'in-progress': 'bg-purple-500/10 text-purple-500 border border-purple-500/30',
  resolved:      'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30',
  closed:        'bg-muted text-muted-foreground border border-border',
  'on-hold':     'bg-amber-500/10 text-amber-500 border border-amber-500/30',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  open:          <AlertCircle className="h-3.5 w-3.5" />,
  'in-progress': <Clock className="h-3.5 w-3.5" />,
  resolved:      <CheckCircle2 className="h-3.5 w-3.5" />,
  closed:        <XCircle className="h-3.5 w-3.5" />,
  'on-hold':     <PauseCircle className="h-3.5 w-3.5" />,
};

const STATUSES = ['open', 'in-progress', 'resolved', 'closed', 'on-hold'];
const FILTERS = [
  { value: 'all', label: 'All', icon: Ticket },
  { value: 'open', label: 'Open', icon: AlertCircle },
  { value: 'in-progress', label: 'In Progress', icon: Clock },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle2 },
  { value: 'on-hold', label: 'On Hold', icon: PauseCircle },
  { value: 'closed', label: 'Closed', icon: XCircle },
];

const Badge = ({ value, styleMap }: { value: string; styleMap: Record<string, string> }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${styleMap[value] || 'bg-muted text-muted-foreground'}`}>
    {STATUS_ICONS[value]}
    {value.replace('-', ' ')}
  </span>
);

const timeAgo = (d: string) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export function AdminTicketsTab({ token }: AdminTicketsTabProps) {
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [selected, setSelected] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editResolutionNotes, setEditResolutionNotes] = useState('');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('all');
  const [success, setSuccess] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch all clients to get ticket list per client
  const fetchAllTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // Get clients first
      const clientsRes = await fetch('/api/clients', { headers: authHeaders, cache: 'no-store' });
      const clientsData = await clientsRes.json();
      const clients: any[] = clientsData.clients || [];

      if (clients.length === 0) {
        setTickets([]);
        setLoading(false);
        return;
      }

      setClientIds(clients.map((c: any) => c.id));

      // Fetch tickets for each client in parallel
      const results = await Promise.all(
        clients.map(async (client: any) => {
          try {
            const url = `/api/clients/${client.id}/tickets${filter !== 'all' ? `?status=${filter}` : ''}`;
            const res = await fetch(url, { headers: authHeaders, cache: 'no-store' });
            if (!res.ok) return [];
            const data = await res.json();
            return (data.tickets || []).map((t: any) => ({ ...t, clientId: client.id, clientName: client.name }));
          } catch {
            return [];
          }
        })
      );

      const all: TicketRow[] = results.flat().sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setTickets(all);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchAllTickets(); }, [fetchAllTickets]);

  const fetchDetail = async (ticket: TicketRow) => {
    try {
      setDetailLoading(true);
      const res = await fetch(`/api/clients/${ticket.clientId}/tickets/${ticket.id}`, { headers: authHeaders, cache: 'no-store' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load ticket');
      const detail: TicketDetail = {
        ...ticket,
        ...data.ticket,
        attachments: data.attachments || [],
        comments: data.comments || [],
      };
      setSelected(detail);
      setEditStatus(detail.status);
      setEditAssignedTo(detail.assignedTo || '');
      setEditResolutionNotes(detail.resolutionNotes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    try {
      setUpdating(true);
      setError('');
      const res = await fetch(`/api/clients/${selected.clientId}/tickets/${selected.id}`, {
        method: 'PUT',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          status: editStatus,
          assignedTo: editAssignedTo,
          resolutionNotes: editResolutionNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setSuccess('Ticket updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      // Refresh detail
      await fetchDetail({ ...selected, status: editStatus as any });
      // Refresh list
      fetchAllTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !newComment.trim()) return;
    try {
      setAddingComment(true);
      const res = await fetch(`/api/clients/${selected.clientId}/tickets/${selected.id}/comments`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ commentText: newComment, isInternal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add comment');
      setNewComment('');
      await fetchDetail(selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Filtered + searched tickets
  const displayed = tickets.filter(t => {
    const matchSearch = !search || [t.title, t.emailAddress, t.companyName, t.id, t.clientName || '']
      .some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const matchClient = selectedClientId === 'all' || t.clientId === selectedClientId;
    return matchSearch && matchClient;
  });

  const inputClass = "w-full px-4 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-sm";
  const labelClass = "block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5";

  // ─── Ticket Detail ────────────────────────────────────────────────────────────
  if (selected) {
    if (detailLoading) return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
      </div>
    );

    return (
      <div className="space-y-4">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-medium transition-colors group">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />Back to All Tickets
        </button>

        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
            <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Left: Ticket Info */}
          <div className="xl:col-span-2 space-y-4">
            {/* Title */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex flex-wrap items-start gap-3 mb-3">
                <h1 className="text-xl font-bold text-foreground flex-1">{selected.title}</h1>
                <Badge value={selected.status} styleMap={STATUS_STYLES} />
                <Badge value={selected.priority} styleMap={PRIORITY_STYLES} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Client</p>
                  <p className="font-medium text-foreground">{selected.clientName || selected.clientId}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Category</p>
                  <p className="font-medium text-foreground">{selected.category}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                  <p className="font-medium text-foreground">{fmtDate(selected.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Last Updated</p>
                  <p className="font-medium text-foreground">{timeAgo(selected.updatedAt || selected.createdAt)}</p>
                </div>
              </div>
              <p className="text-xs font-mono text-muted-foreground mt-3">ID: {selected.id}</p>
            </div>

            {/* Submitter Info */}
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Submitted By</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  [<User className="h-3.5 w-3.5" />, 'Name', selected.queryName || 'N/A'],
                  [<Building2 className="h-3.5 w-3.5" />, 'Company', selected.companyName || 'N/A'],
                  [<Mail className="h-3.5 w-3.5" />, 'Email', selected.emailAddress],
                  [<Phone className="h-3.5 w-3.5" />, 'Phone', selected.phoneNumber || 'N/A'],
                ].map(([icon, label, val]) => (
                  <div key={label as string} className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5 shrink-0">{icon}</span>
                    <div>
                      <p className="text-xs text-muted-foreground">{label as string}</p>
                      <p className="font-medium text-foreground break-all">{val as string}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Description</h3>
              <p className="text-foreground whitespace-pre-wrap text-sm leading-relaxed">{selected.description || 'No description provided.'}</p>
            </div>

            {/* Attachments */}
            {selected.attachments?.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Attachments ({selected.attachments.length})</h3>
                <div className="space-y-2">
                  {selected.attachments.map(a => (
                    <div key={a.id} className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{a.fileName}</p>
                        <p className="text-xs text-muted-foreground">{(a.fileSize / 1024 / 1024).toFixed(2)} MB · {fmtDate(a.createdAt)}</p>
                      </div>
                      <a href={a.filePath} download className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground">
                        <Download className="h-4 w-4" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution Notes (display only) */}
            {selected.resolutionNotes && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-3">Resolution Notes</h3>
                <p className="text-foreground whitespace-pre-wrap text-sm">{selected.resolutionNotes}</p>
              </div>
            )}

            {/* Comments */}
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Messages & Comments ({selected.comments?.length || 0})</h3>
              <div className="space-y-3 mb-5 max-h-80 overflow-y-auto pr-1">
                {selected.comments?.length > 0 ? selected.comments.map(c => (
                  <div key={c.id} className={`p-4 rounded-xl text-sm ${c.isInternal ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-blue-500/5 border border-blue-500/15'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">{c.commentedBy}</span>
                        {c.isInternal && <span className="text-xs bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full border border-amber-500/20">Internal</span>}
                      </div>
                      <span className="text-xs text-muted-foreground">{timeAgo(c.createdAt)}</span>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">{c.text}</p>
                  </div>
                )) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <MessageSquare className="mx-auto h-7 w-7 mb-2 opacity-30" />
                    <p className="text-sm">No comments yet</p>
                  </div>
                )}
              </div>

              {/* Add Comment */}
              <form onSubmit={handleAddComment} className="border-t border-border pt-4 space-y-3">
                <label className={labelClass}>Add Reply / Note</label>
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Type your reply or internal note..."
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={e => setIsInternal(e.target.checked)}
                      className="rounded"
                    />
                    Internal note (not visible to client)
                  </label>
                  <Button type="submit" disabled={addingComment || !newComment.trim()} size="sm" className="gap-2">
                    {addingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {isInternal ? 'Add Note' : 'Send Reply'}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Right: Manage Panel */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm sticky top-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Manage Ticket</h3>
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label className={labelClass}>Status</label>
                  <div className="space-y-2">
                    {STATUSES.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setEditStatus(s)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                          editStatus === s
                            ? STATUS_STYLES[s]
                            : 'border-border text-muted-foreground hover:bg-accent bg-background'
                        }`}
                      >
                        {STATUS_ICONS[s]}
                        {s.replace('-', ' ')}
                        {editStatus === s && <span className="ml-auto text-xs">• Selected</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assigned To */}
                <div>
                  <label className={labelClass}>Assigned To</label>
                  <input
                    value={editAssignedTo}
                    onChange={e => setEditAssignedTo(e.target.value)}
                    placeholder="Engineer name or email"
                    className={inputClass}
                  />
                </div>

                {/* Resolution Notes */}
                <div>
                  <label className={labelClass}>Resolution Notes</label>
                  <textarea
                    value={editResolutionNotes}
                    onChange={e => setEditResolutionNotes(e.target.value)}
                    placeholder="Describe how the issue was resolved..."
                    rows={4}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <Button onClick={handleUpdate} disabled={updating} className="w-full gap-2">
                  {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Ticket List ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Ticket className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Support Tickets</h1>
            <p className="text-muted-foreground text-sm">{tickets.length} total · {tickets.filter(t => t.status === 'open').length} open</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAllTickets} className="gap-2">
          <RefreshCw className="h-4 w-4" />Refresh
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Open',        count: tickets.filter(t => t.status === 'open').length,          color: 'text-blue-500',    bg: 'bg-blue-500/10' },
          { label: 'In Progress', count: tickets.filter(t => t.status === 'in-progress').length,   color: 'text-purple-500',  bg: 'bg-purple-500/10' },
          { label: 'On Hold',     count: tickets.filter(t => t.status === 'on-hold').length,       color: 'text-amber-500',   bg: 'bg-amber-500/10' },
          { label: 'Resolved',    count: tickets.filter(t => t.status === 'resolved').length,      color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
          { label: 'Closed',      count: tickets.filter(t => t.status === 'closed').length,        color: 'text-muted-foreground', bg: 'bg-muted' },
        ].map(s => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-4 text-center">
            <p className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.count}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title, email, company..."
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => {
            const Icon = f.icon;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                  filter === f.value
                    ? 'bg-blue-500/10 border-blue-500/40 text-blue-500'
                    : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Ticket Table */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 text-center">
            <Loader2 className="mx-auto h-10 w-10 text-blue-500 animate-spin mb-4" />
            <p className="text-muted-foreground">Loading tickets from all clients...</p>
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-16 text-center">
            <Ticket className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No tickets found</h3>
            <p className="text-muted-foreground text-sm">{search ? 'Try a different search term.' : 'No tickets match the current filter.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-muted/30">
                <tr>
                  {['Ticket', 'Client', 'Submitted By', 'Priority', 'Status', 'Created', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayed.map((t, i) => (
                  <motion.tr
                    key={t.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="hover:bg-accent/40 cursor-pointer transition-colors"
                    onClick={() => fetchDetail(t)}
                  >
                    <td className="px-4 py-4 max-w-xs">
                      <p className="font-medium text-foreground truncate">{t.title}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">{t.id.slice(0, 20)}…</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-foreground">{t.clientName || t.clientId}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-foreground">{t.queryName || t.emailAddress}</p>
                      <p className="text-xs text-muted-foreground">{t.emailAddress}</p>
                    </td>
                    <td className="px-4 py-4">
                      <Badge value={t.priority} styleMap={PRIORITY_STYLES} />
                    </td>
                    <td className="px-4 py-4">
                      <Badge value={t.status} styleMap={STATUS_STYLES} />
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground whitespace-nowrap">
                      <p>{timeAgo(t.createdAt)}</p>
                      <p className="text-xs">{new Date(t.createdAt).toLocaleDateString()}</p>
                    </td>
                    <td className="px-4 py-4">
                      <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); fetchDetail(t); }} className="text-xs gap-1">
                        Manage <ChevronDown className="h-3 w-3" />
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
